import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Search, Users, Key, Link2, X, BookOpen, Copy, Eye, EyeOff, Trash2, CheckSquare, Square, Check } from 'lucide-react';
import { toast } from 'sonner';
import { successToast } from '../../lib/successToast';
import { HomeUniLoader } from '../../components/HomeUniLoader';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from '../../components/ui/alert-dialog';

export default function ParentManagement() {
  const [managers, setManagers] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [linkDialog, setLinkDialog] = useState(null); // manager object
  const [linkSelected, setLinkSelected] = useState([]); // programme_ids being linked
  const [form, setForm] = useState({ name: '', email: '', password: 'pm123456', phone: '', programme_ids: [] });
  const [progSearch, setProgSearch] = useState('');
  const [activeTab, setActiveTab] = useState('manage');
  const [showPasswords, setShowPasswords] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [mgrRes, progRes] = await Promise.all([
        api.get('/auth/users', { params: { role: 'ProgrammeManager' } }),
        api.get('/academic/programmes'),
      ]);
      const progMap = Object.fromEntries((progRes.data || []).map(p => [p.programme_id, p]));
      const enriched = (mgrRes.data || []).map(m => {
        const pids = m.programme_ids || [];
        const linkedProgrammes = pids.map(id => progMap[id]).filter(Boolean);
        return { ...m, linkedProgrammes };
      });
      setManagers(enriched);
      setProgrammes(progRes.data || []);
    } catch (err) { console.error('Error:', err); }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/auth/register', { ...form, role: 'ProgrammeManager' });
      if (form.programme_ids.length > 0) {
        await api.put(`/auth/users/${res.data.user.user_id}`, { programme_ids: form.programme_ids });
      }
      successToast(`Programme Manager account created: ${form.email} / ${form.password}`);
      setCreateDialog(false);
      setForm({ name: '', email: '', password: 'pm123456', phone: '', programme_ids: [] });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleLinkProgrammes = async () => {
    if (!linkDialog) return;
    try {
      await api.put(`/auth/users/${linkDialog.user_id}`, { programme_ids: linkSelected });
      successToast('Programmes updated');
      setLinkDialog(null);
      fetchData();
    } catch { toast.error('Failed to update programmes'); }
  };

  const handleUnlinkProgramme = async (manager, progId) => {
    try {
      const updated = (manager.programme_ids || []).filter(id => id !== progId);
      await api.put(`/auth/users/${manager.user_id}`, { programme_ids: updated });
      successToast('Programme unlinked');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const toggleSelect = (uid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filtered.map(m => m.user_id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setDeleting(true);
    try {
      const r = await api.post('/auth/users/bulk-delete', { user_ids: ids });
      successToast(`Deleted ${r.data.deleted_count} account(s)`);
      clearSelection();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    setDeleting(false);
  };

  const handleDeleteOne = async (uid, name) => {
    try {
      await api.delete(`/auth/users/${uid}`);
      successToast(`Deleted ${name}`);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(uid); return n; });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const toggleFormProgramme = (pid) => {
    setForm(f => ({
      ...f,
      programme_ids: f.programme_ids.includes(pid)
        ? f.programme_ids.filter(id => id !== pid)
        : [...f.programme_ids, pid],
    }));
  };

  const filtered = managers.filter(m => {
    const q = search.toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) ||
      (m.linkedProgrammes || []).some(p => p.name?.toLowerCase().includes(q));
  });

  const progSearchResults = programmes.filter(p =>
    progSearch && p.name.toLowerCase().includes(progSearch.toLowerCase())
  );

  if (loading) return <HomeUniLoader />;

  return (
    <div className="space-y-6" data-testid="parent-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1565C0]">Programme Manager Management</h1>
          <p className="text-slate-500 text-sm mt-1">{managers.length} programme manager account{managers.length !== 1 ? 's' : ''}</p>
        </div>
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-md bg-[#2E6CC4]" data-testid="add-parent-btn">
              <Plus className="w-4 h-4 mr-2" /> New Programme Manager
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Programme Manager</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="e.g. Dr. Abena Mensah" data-testid="parent-name" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+233..." data-testid="parent-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email (Login)</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="manager@homeuni.edu.gh" data-testid="parent-email" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input value={form.password} onChange={e => setForm({...form, password: e.target.value})} required data-testid="parent-password" />
                </div>
              </div>

              {/* Link Programmes */}
              <div className="space-y-2">
                <Label>Assign to Programme(s)</Label>
                <div className="flex items-center gap-2 bg-slate-50 rounded-md border border-slate-200 px-3 py-2">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={progSearch}
                    onChange={e => setProgSearch(e.target.value)}
                    placeholder="Search programme..."
                    className="bg-transparent border-none outline-none text-sm w-full"
                  />
                </div>
                {progSearch && (
                  <div className="bg-white border border-slate-200 rounded-md shadow-md max-h-36 overflow-y-auto">
                    {(progSearchResults.length > 0 ? progSearchResults : programmes).slice(0, 8).map(p => (
                      <div
                        key={p.programme_id}
                        onClick={() => { toggleFormProgramme(p.programme_id); setProgSearch(''); }}
                        className="px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer flex justify-between items-center"
                      >
                        <span className="font-medium">{p.name}</span>
                        {form.programme_ids.includes(p.programme_id) && <Check className="w-3.5 h-3.5 text-[#2E6CC4]" />}
                      </div>
                    ))}
                  </div>
                )}
                {!progSearch && (
                  <div className="bg-white border border-slate-200 rounded-md max-h-40 overflow-y-auto">
                    {programmes.map(p => (
                      <label key={p.programme_id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 text-xs">
                        <input
                          type="checkbox"
                          checked={form.programme_ids.includes(p.programme_id)}
                          onChange={() => toggleFormProgramme(p.programme_id)}
                          className="rounded"
                        />
                        <span className="font-medium">{p.name}</span>
                        <span className="text-slate-400 ml-auto">{p.code}</span>
                      </label>
                    ))}
                  </div>
                )}
                {form.programme_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {form.programme_ids.map(id => {
                      const prog = programmes.find(p => p.programme_id === id);
                      return prog ? (
                        <Badge key={id} className="bg-blue-100 text-blue-700 flex items-center gap-1 text-[10px]">
                          <BookOpen className="w-2.5 h-2.5" /> {prog.name}
                          <X className="w-2.5 h-2.5 cursor-pointer ml-0.5" onClick={() => toggleFormProgramme(id)} />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 rounded-md p-3 text-xs text-blue-700">
                <strong>Login details:</strong> {form.email || '[email]'} / {form.password}
                <br />Programme Manager will have oversight of the assigned programme(s).
              </div>
              <Button type="submit" className="w-full rounded-md bg-[#2E6CC4]" data-testid="submit-parent">
                Create Programme Manager Account
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-md w-fit">
        <button
          onClick={() => setActiveTab('manage')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'manage' ? 'bg-white text-[#1565C0] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-manage"
        >
          <Users className="w-4 h-4 inline mr-1.5" /> Manage ({managers.length})
        </button>
        <button
          onClick={() => setActiveTab('logins')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'logins' ? 'bg-white text-[#1565C0] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-logins"
        >
          <Key className="w-4 h-4 inline mr-1.5" /> Manage Logins
        </button>
      </div>

      {/* ── MANAGE LOGINS TAB ── */}
      {activeTab === 'logins' && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 bg-white rounded-md border border-slate-200 px-3 py-2 flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, or programme..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="bg-transparent border-none outline-none text-sm w-full"
                data-testid="login-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowPasswords(!showPasswords)} data-testid="toggle-passwords">
                {showPasswords ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                {showPasswords ? 'Hide' : 'Show'} Passwords
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => {
                const rows = filtered.map((m, i) => `${i+1}\t${m.name}\t${m.email}\tpm123456\t${(m.linkedProgrammes || []).map(p => p.name).join(', ')}`);
                navigator.clipboard.writeText(`#\tName\tEmail\tPassword\tProgrammes\n${rows.join('\n')}`);
                successToast('All login details copied to clipboard');
              }} data-testid="copy-all-logins">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy All
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-[#1565C0] text-white text-xs w-10">#</TableHead>
                  <TableHead className="bg-[#1565C0] text-white text-xs">Programme Manager</TableHead>
                  <TableHead className="bg-[#1565C0] text-white text-xs">Email (Login)</TableHead>
                  <TableHead className="bg-[#1565C0] text-white text-xs">Password</TableHead>
                  <TableHead className="bg-[#1565C0] text-white text-xs">Phone</TableHead>
                  <TableHead className="bg-[#1565C0] text-white text-xs">Programme(s)</TableHead>
                  <TableHead className="bg-[#1565C0] text-white text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400 text-sm">No accounts match your search</TableCell></TableRow>
                ) : filtered.map((m, idx) => (
                  <TableRow key={m.user_id} className={idx % 2 === 0 ? '' : 'bg-slate-50/50'} data-testid={`login-row-${m.user_id}`}>
                    <TableCell className="text-xs text-slate-400 font-mono">{idx + 1}</TableCell>
                    <TableCell className="font-medium text-sm text-[#1565C0]">{m.name}</TableCell>
                    <TableCell className="text-sm font-mono text-[#2E6CC4]">{m.email}</TableCell>
                    <TableCell className="text-sm font-mono">{showPasswords ? 'pm123456' : '••••••••'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{m.phone || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(m.linkedProgrammes || []).map(p => (
                          <Badge key={p.programme_id} variant="secondary" className="text-[9px]">{p.name}</Badge>
                        ))}
                        {!(m.linkedProgrammes || []).length && <span className="text-[10px] text-red-400">No programme linked</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => { navigator.clipboard.writeText(`Email: ${m.email}\nPassword: pm123456`); successToast('Copied'); }}
                        className="p-1 text-slate-400 hover:text-[#2E6CC4] rounded"
                        title="Copy login"
                        data-testid={`copy-login-${m.user_id}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[10px] text-slate-400 text-right">{filtered.length} account{filtered.length !== 1 ? 's' : ''} shown · Default password: pm123456</p>
        </div>
      )}

      {/* ── MANAGE TAB ── */}
      {activeTab === 'manage' && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-white rounded-md border border-slate-200 px-3 py-2 max-w-md flex-1">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search programme managers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="bg-transparent border-none outline-none text-sm w-full"
              />
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-1.5 animate-fade-in" data-testid="bulk-action-bar">
                <span className="text-xs font-medium text-red-700">{selectedIds.size} selected</span>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-600" onClick={clearSelection}>
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={deleting} data-testid="bulk-delete-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> Delete {selectedIds.size}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {selectedIds.size} account{selectedIds.size > 1 ? 's' : ''}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the selected programme manager login(s). This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleBulkDelete}>
                        Delete {selectedIds.size}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>

          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-slate-50 w-10">
                    <button onClick={toggleSelectAllVisible} className="p-1 rounded hover:bg-slate-200 transition-colors" aria-label="Select all">
                      {filtered.length > 0 && filtered.every(m => selectedIds.has(m.user_id))
                        ? <CheckSquare className="w-4 h-4 text-[#2E6CC4]" />
                        : <Square className="w-4 h-4 text-slate-400" />}
                    </button>
                  </TableHead>
                  <TableHead className="bg-slate-50">Programme Manager</TableHead>
                  <TableHead className="bg-slate-50">Email (Login)</TableHead>
                  <TableHead className="bg-slate-50">Phone</TableHead>
                  <TableHead className="bg-slate-50">Programme(s)</TableHead>
                  <TableHead className="bg-slate-50">Password</TableHead>
                  <TableHead className="bg-slate-50">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(m => (
                  <TableRow key={m.user_id} data-testid={`parent-row-${m.user_id}`} className={selectedIds.has(m.user_id) ? 'bg-blue-50/60' : ''}>
                    <TableCell className="w-10">
                      <button onClick={() => toggleSelect(m.user_id)} className="p-1 rounded hover:bg-slate-200 transition-colors" aria-label="Select row">
                        {selectedIds.has(m.user_id)
                          ? <CheckSquare className="w-4 h-4 text-[#2E6CC4]" />
                          : <Square className="w-4 h-4 text-slate-400" />}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-sm font-mono text-[#2E6CC4]">{m.email}</TableCell>
                    <TableCell className="text-sm text-slate-500">{m.phone || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(m.linkedProgrammes || []).map(p => (
                          <Badge key={p.programme_id} variant="secondary" className="text-[10px] flex items-center gap-1">
                            <BookOpen className="w-2.5 h-2.5" /> {p.name}
                            <X className="w-2.5 h-2.5 cursor-pointer ml-0.5" onClick={() => handleUnlinkProgramme(m, p.programme_id)} />
                          </Badge>
                        ))}
                        {!(m.linkedProgrammes || []).length && <span className="text-xs text-slate-400">No programmes linked</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">pm123456</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 rounded-md"
                          onClick={() => {
                            setLinkSelected(m.programme_ids || []);
                            setLinkDialog(m);
                          }}
                          data-testid={`link-ward-${m.user_id}`}
                        >
                          <Link2 className="w-3 h-3 mr-1" /> Link Programmes
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors" title={`Delete ${m.name}`} data-testid={`delete-parent-${m.user_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {m.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes the login for <strong>{m.email}</strong>.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteOne(m.user_id, m.name)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No programme manager accounts yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Link Programmes Dialog ── */}
      <Dialog open={!!linkDialog} onOpenChange={() => setLinkDialog(null)}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link Programmes — {linkDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Select all programmes this manager oversees. Changes replace the current assignment.</p>
            <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto divide-y divide-slate-100">
              {programmes.map(p => (
                <label key={p.programme_id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50 text-sm">
                  <input
                    type="checkbox"
                    checked={linkSelected.includes(p.programme_id)}
                    onChange={() => setLinkSelected(prev =>
                      prev.includes(p.programme_id)
                        ? prev.filter(id => id !== p.programme_id)
                        : [...prev, p.programme_id]
                    )}
                    className="rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1565C0] truncate">{p.name}</p>
                    <p className="text-[10px] text-slate-400">{p.code} · {p.department}</p>
                  </div>
                  {linkSelected.includes(p.programme_id) && <Check className="w-4 h-4 text-[#2E6CC4] flex-shrink-0" />}
                </label>
              ))}
              {programmes.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No programmes found. Add programmes in Academics first.</p>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setLinkDialog(null)}>Cancel</Button>
              <Button className="flex-1 rounded-md bg-[#2E6CC4]" onClick={handleLinkProgrammes}>
                <Link2 className="w-4 h-4 mr-2" /> Save ({linkSelected.length} selected)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
